import { useEffect, useState } from 'react';
import type { Extension } from '@arcnet/contracts';
import {
  useAppDevices, useAppRegistrations, useCreateAppCode, useSignOutAppDevice,
} from '../../lib/api/hooks-extension.js';
import { useSession } from '../../lib/auth/session.js';
import { Badge, ErrorBanner } from '../../components/ui/Primitives.js';
import { relativeTime } from '../../lib/format.js';
import { QrCode } from './MobileProvisioning.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { toastError } from '../../components/ui/Toast.js';

/**
 * The ArcSIP app on this extension (docs/34): a one-time sign-in code, and the phones it is
 * signed in on. The phone registers with the extension's main registration, or another of its own
 * chosen here (owner, 18 September 2026). The code works once, for ten minutes; the person the
 * extension belongs to can also sign in with their own TMS login in the app.
 *
 * Signing a phone out ends its session with the platform and the app stops; the password it
 * holds is the extension's, so a phone that is lost is cut off by issuing a new password.
 */
export function AppSignIn({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const can = useSession((s) => s.can);
  const me = useSession((s) => s.session?.user.id);
  // Their own linked seat; anyone else's needs the right to reveal its password, since the code is
  // redeemed for it (docs/34 D3). The API decides either way.
  const ownSeat = !can('extension:write') && can('extension:write:own') && ext.user_id === me;
  const mayCode = ownSeat || can('extension:credentials:reveal');
  const mayWrite = can('extension:write') || ownSeat;
  const step = useStepUp();
  const devices = useAppDevices(tenantId, ext.id);
  const regs = useAppRegistrations(tenantId, ext.id);
  const usable = (regs.data?.data ?? []).filter((r) => r.available);
  const [regId, setRegId] = useState<string | null>(null);
  const create = useCreateAppCode(tenantId, ext.id);
  const signOut = useSignOutAppDevice(tenantId, ext.id);
  const [code, setCode] = useState<{ uri: string; username: string; until: number } | null>(null);
  const [left, setLeft] = useState(0);

  useEffect(() => {
    if (!code) return;
    const tick = () => {
      const s = Math.max(0, Math.round((code.until - Date.now()) / 1000));
      setLeft(s);
      if (s === 0) setCode(null);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [code]);

  const phones = devices.data?.data ?? [];

  return (
    <>
      <StepUpPrompt step={step} what="Showing a sign-in code" />
      {signOut.error && <ErrorBanner error={signOut.error} />}

      {code ? (
        <div className="arc-row" style={{ gap: 20, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 14 }}>
          <QrCode text={code.uri} />
          <div className="arc-stack" style={{ gap: 8, minWidth: 200, flex: 1 }}>
            <span style={{ fontWeight: 550 }}>Scan in the ArcSIP app</span>
            <span className="arc-muted" style={{ fontSize: 12.5 }}>
              Signs one phone in to {ext.number} as <span className="arc-mono">{code.username}</span>. Works once.
            </span>
            <span className="arc-subtle arc-num" style={{ fontSize: 12 }}>
              Expires in {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}
            </span>
            <div>
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => setCode(null)}>Hide</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="arc-row" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <p className="arc-muted" style={{ margin: 0, fontSize: 12.5, flex: '1 1 260px' }}>
            Sign in with a code, or with {ext.user_id ? 'their' : 'a'} TMS login in the app
            {ext.user_id ? '' : ' once this extension is linked to a person'}.
          </p>
          {mayCode && usable.length > 1 && (
            <select className="arc-select" style={{ width: 'auto', minWidth: 160 }} aria-label="Registration"
              value={regId ?? usable[0]!.id} onChange={(e) => setRegId(e.target.value)}>
              {usable.map((r) => (
                <option key={r.id} value={r.id}>{r.username}{r.primary ? ' (main)' : ''}</option>
              ))}
            </select>
          )}
          {mayCode && (
            <button type="button" className="arc-btn arc-btn-sm"
              disabled={create.isPending || (regs.data !== undefined && usable.length === 0)}
              onClick={() => {
                // As the manual setup code: the step-up prompt replays the action after the password.
                const make = async () => {
                  const r = await create.mutateAsync(regId);
                  setCode({ uri: r.uri, username: r.username, until: Date.parse(r.expires_at) });
                  return r;
                };
                (ownSeat ? make() : step.run(make)).catch(toastError);
              }}>
              {create.isPending ? 'Creating…' : 'Show sign-in code'}
            </button>
          )}
        </div>
      )}

      {phones.length > 0 && (
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr><th>Phone</th><th>Registration</th><th>Signed in</th><th>Last seen</th><th /></tr>
            </thead>
            <tbody>
              {phones.map((d) => (
                <tr key={d.id}>
                  <td>
                    <div className="arc-stack">
                      <span style={{ fontWeight: 550 }}>{d.label ?? 'ArcSIP app'}</span>
                      <span className="arc-subtle">
                        {d.platform === 'ios' ? 'iOS' : d.platform === 'android' ? 'Android' : ''}
                        {d.app_version ? ` · ${d.app_version}` : ''}
                      </span>
                    </div>
                  </td>
                  <td className="arc-mono">{d.username ?? ''}</td>
                  <td>
                    <div className="arc-stack">
                      <span>{relativeTime(d.created_at)}</span>
                      <span className="arc-subtle">
                        {d.enrolled_via === 'qr' ? <Badge tone="muted">code</Badge> : d.user_email}
                      </span>
                    </div>
                  </td>
                  <td className="arc-muted">{d.last_seen_at ? relativeTime(d.last_seen_at) : 'never'}</td>
                  <td className="arc-td-right">
                    {mayWrite && (
                      <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={signOut.isPending}
                        onClick={() => {
                          if (confirm(`Sign ${d.label ?? 'this phone'} out? The app stops using this extension. `
                            + 'If the phone is lost, also issue a new password: it holds the current one.')) {
                            signOut.mutate(d.id);
                          }
                        }}>
                        Sign out
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
